import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { VStack, Text, Button, Box } from '@chakra-ui/react';

export interface TransitionScreenProps {
  titleKey: string;
  subtitleKey: string;
  illustration?: React.ReactNode;
  skippable?: boolean;
  onContinue: () => void;
  onSkip?: () => void;
}

export function TransitionScreen({
  titleKey,
  subtitleKey,
  illustration,
  skippable = false,
  onContinue,
  onSkip,
}: TransitionScreenProps) {
  const { t } = useTranslation();

  const handleContinue = useCallback(() => {
    onContinue();
  }, [onContinue]);

  const handleSkip = useCallback(() => {
    onSkip?.();
  }, [onSkip]);

  return (
    <VStack
      flex={1}
      justify="center"
      align="center"
      p={6}
      spacing={6}
      bg="white"
      data-testid="transition-screen"
    >
      {illustration && <Box mb={4}>{illustration}</Box>}

      <VStack spacing={3} align="center" maxW="400px">
        <Text fontSize="2xl" fontWeight="700" textAlign="center">
          {t(titleKey)}
        </Text>
        <Text fontSize="md" color="gray.500" textAlign="center" lineHeight="22px">
          {t(subtitleKey)}
        </Text>
      </VStack>

      <VStack w="100%" spacing={3} mt={6} maxW="320px">
        <Button
          size="lg"
          colorScheme="blue"
          w="100%"
          h="52px"
          borderRadius="lg"
          fontWeight="bold"
          onClick={handleContinue}
          data-testid="transition-continue-button"
        >
          {t('common.continue', 'Continue')}
        </Button>

        {skippable && onSkip && (
          <Button
            size="md"
            variant="ghost"
            h="44px"
            borderRadius="lg"
            onClick={handleSkip}
            data-testid="transition-skip-button"
          >
            <Text color="gray.500" fontSize="sm">
              {t('common.skip', 'Skip')}
            </Text>
          </Button>
        )}
      </VStack>
    </VStack>
  );
}

export default TransitionScreen;
